import { useRef } from 'react'
import { Trash2, Moon } from 'lucide-react'
import { isHorizontalSwipe, clampTravel, settleTarget } from '../lib/swipe'

// Unified row gesture primitive: tap, long-press, and horizontal swipe share ONE
// pointer handler so they cooperate instead of fighting.
//   swipe LEFT  -> reveal Delete zone on the right edge
//   swipe RIGHT -> reveal Tonight zone on the left edge
// Vertical drags are handed back to the page so normal scrolling is untouched.
//
// Native-feel engine (2026-07-21): the drag is written STRAIGHT to the DOM as a
// GPU `translate3d` inside a requestAnimationFrame — no React state per frame (the
// old `setTx` re-rendered the row every move) and no animating `left` (a layout
// property that reflowed every frame). Position/open-state live in refs; the only
// React state is none. Result: the finger tracks 1:1 and the release springs.
const SPRING = 'transform 0.34s cubic-bezier(0.22, 1, 0.36, 1)'

// The right-edge zone is parameterised because not every row's destructive action is
// "delete this show". Tonight's Lineup removes a PLAN — the show stays on your list,
// untouched — and a red trash can there would be a lie that costs someone their
// watchlist entry in their head before they even tap. Pass deleteLabel/DeleteIcon/
// deleteTone to say what the row actually does.
//
// When onTonight is omitted the left zone isn't rendered AND travel is clamped to the
// left only, so a right-swipe can't drag open an empty amber panel with no action
// behind it (a row already planned for tonight has nothing to offer there).
export function SwipeRow({
  children, onTap, onLongPress, onDelete, onTonight, tonightActive = false,
  disabled = false, actionWidth = 88,
  deleteLabel = 'Delete', DeleteIcon = Trash2, deleteTone = 'var(--state-bad)',
  // The zone hard-coded white text, which is right on the red delete tone and wrong on
  // anything light: "Not tonight" on amber came out white-on-gold and was hard to read
  // (Jason 2026-08-01). Amber surfaces in this app take --amber-ink, same as every
  // primary button; pass the ink alongside the tone.
  deleteInk = '#fff',
  // The right-swipe zone defaults to Tonight (reel). Tonight itself reuses the zone
  // for "Tomorrow" on a plan row, so the label and icon are overridable.
  tonightLabel, TonightIcon,
}) {
  const slideRef  = useRef(null)
  const st        = useRef(null)
  const longTimer = useRef(null)
  const moved     = useRef(false)
  const openRef   = useRef(0)   // -1 open-left(delete) / 0 closed / 1 open-right(tonight)
  const txRef     = useRef(0)   // current translateX

  // Follow-the-finger: write the transform SYNCHRONOUSLY on each move — touch/pointer
  // events are already frame-aligned on iOS, so rAF only adds a frame of lag (the jitter).
  // Round to whole pixels so text doesn't re-rasterize (shimmer) on sub-pixel offsets.
  const draw = (x) => { txRef.current = x; const el = slideRef.current; if (el) el.style.transform = `translate3d(${Math.round(x)}px,0,0)` }
  // Settle with a spring (or snap instantly when spring=false).
  const apply = (x, spring) => {
    const el = slideRef.current; if (!el) return
    el.style.transition = spring ? SPRING : 'none'
    el.style.transform  = `translate3d(${Math.round(x)}px,0,0)`
    txRef.current = x
  }

  const clearLong = () => { if (longTimer.current) { clearTimeout(longTimer.current); longTimer.current = null } }
  const settle = (v) => { apply(v, true); openRef.current = v < 0 ? -1 : v > 0 ? 1 : 0 }
  const close  = () => settle(0)

  const onPointerDown = (e) => {
    if (e.target && e.target.closest && e.target.closest('[data-noswipe]')) { st.current = null; return }
    st.current = { x: e.clientX, y: e.clientY, base: txRef.current, axis: null }
    moved.current = false
    clearLong()
    // Grab any in-flight settle instantly — kill the transition so the finger owns it.
    const el = slideRef.current; if (el) el.style.transition = 'none'
    if (!disabled && onLongPress) {
      longTimer.current = setTimeout(() => {
        if (st.current && st.current.axis === null && !moved.current) {
          moved.current = true
          st.current = null
          settle(0)
          onLongPress()
        }
      }, 480)
    }
  }

  const onPointerMove = (e) => {
    const s = st.current; if (!s) return
    const dx = e.clientX - s.x, dy = e.clientY - s.y
    if (disabled) { if (Math.abs(dx) > 10 || Math.abs(dy) > 10) moved.current = true; return }
    if (s.axis === null) {
      if (Math.abs(dx) < 7 && Math.abs(dy) < 7) return
      // Axis lock (tested: lib/swipe.isHorizontalSwipe). ratio 1.0 = classic dx>dy.
      s.axis = isHorizontalSwipe(dx, dy, 1.0) ? 'x' : 'y'
      clearLong()
      if (s.axis === 'x') { try { e.currentTarget.setPointerCapture(e.pointerId) } catch (err) {} }
    }
    if (s.axis !== 'x') return
    e.preventDefault()
    moved.current = true
    draw(clampTravel(s.base + dx, onTonight ? 'both' : 'left', actionWidth))
  }

  const onPointerUp = () => {
    const s = st.current; st.current = null
    clearLong()
    if (!s) return
    if (s.axis !== 'x') {
      if (moved.current) return
      if (openRef.current !== 0) { close(); return }
      onTap && onTap()
      return
    }
    // Snap open/closed (tested: lib/swipe.settleTarget, 50% open ratio).
    settle(settleTarget(txRef.current, actionWidth))
  }

  const swallow = (e) => { if (moved.current || openRef.current !== 0) { e.stopPropagation(); e.preventDefault() } }

  return (
    <div style={{ position: 'relative', overflow: 'hidden', borderRadius: 13, touchAction: 'pan-y' }}>
      {onTonight && (
        <div onClick={() => { onTonight(); close() }} style={{ position: 'absolute', top: 0, bottom: 0, left: 0, width: actionWidth, background: tonightActive ? 'rgba(var(--amber-rgb),0.32)' : 'rgba(var(--amber-rgb),0.92)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 3, color: 'var(--amber-ink)', cursor: 'pointer' }}>
          {TonightIcon ? <TonightIcon size={22} color='var(--amber-ink)' /> : <Moon size={22} color='var(--amber-ink)' strokeWidth={2} />}
          <span style={{ fontSize: 11, fontWeight: 700, fontFamily: 'var(--font)' }}>{tonightLabel || (tonightActive ? 'Planned' : 'Tonight')}</span>
        </div>
      )}
      <div onClick={() => { onDelete && onDelete(); close() }} style={{ position: 'absolute', top: 0, bottom: 0, right: 0, width: actionWidth, background: `color-mix(in srgb, ${deleteTone} 95%, transparent)`, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 3, color: deleteInk, cursor: 'pointer' }}>
        <DeleteIcon size={20} />
        <span style={{ fontSize: 11, fontWeight: 700, fontFamily: 'var(--font)' }}>{deleteLabel}</span>
      </div>
      <div ref={slideRef} onPointerDown={onPointerDown} onPointerMove={onPointerMove} onPointerUp={onPointerUp} onPointerCancel={onPointerUp} onClickCapture={swallow} style={{ position: 'relative', touchAction: 'pan-y', background: 'var(--bg)', willChange: 'transform' }}>
        {children}
      </div>
    </div>
  )
}
